/**
 * Comprehensive validation messages with detailed explanations
 * Provides concise, actionable error messages with examples
 */

export interface ValidationMessage {
    error: string;        // Main error message
    example?: string;     // Example of valid input
    helpText?: string;    // Additional help text for hints
}

export interface FieldValidationRules {
    required?: boolean;
    pattern?: RegExp;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    custom?: string[];    // Custom validation rules as text
}

/**
 * Field-specific validation rules and messages
 */
const FIELD_VALIDATION_MAP: Record<string, FieldValidationRules & { label: string }> = {
    // Company fields
    'tax_id': {
        label: 'เลขประจำตัวผู้เสียภาษี',
        required: true,
        pattern: /^\d{13}$/,
        custom: ['ต้องเป็นตัวเลข 13 หลัก', 'ห้ามซ้ำกับที่มีในระบบ'],
    },
    'company_code': {
        label: 'รหัสบริษัท',
        required: true,
        maxLength: 50,
        custom: ['ห้ามซ้ำกับที่มีในระบบ'],
    },
    'name': {
        label: 'ชื่อบริษัท',
        required: true,
        maxLength: 255,
    },
    'email': {
        label: 'อีเมล',
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        custom: ['ต้องมี @ และชื่อโดเมน'],
    },
    'phone': {
        label: 'เบอร์โทรศัพท์',
        pattern: /^(?:0\d{1,2}-?\d{3}-?\d{4}|0\d{9})$/,
        custom: ['รูปแบบ: 02-xxx-xxxx หรือ 08x-xxx-xxxx'],
    },
    'postal_code': {
        label: 'รหัสไปรษณีย์',
        pattern: /^\d{5}$/,
        custom: ['ต้องเป็นตัวเลข 5 หลัก'],
    },

    // Customer fields
    'id_card_number': {
        label: 'เลขบัตรประชาชน',
        required: true,
        pattern: /^\d{13}$/,
        custom: ['ต้องเป็นตัวเลข 13 หลัก'],
    },
    'first_name': {
        label: 'ชื่อ',
        required: true,
        maxLength: 100,
    },
    'last_name': {
        label: 'นามสกุล',
        required: true,
        maxLength: 100,
    },

    // Agent fields
    'agent_id': {
        label: 'รหัสตัวแทน',
        required: true,
        maxLength: 50,
        custom: ['ห้ามซ้ำกับที่มีในระบบ'],
    },
    'full_name': {
        label: 'ชื่อ-นามสกุล',
        required: true,
        maxLength: 255,
    },
    'agent_code': {
        label: 'รหัสตัวแทน',
        required: true,
        maxLength: 50,
        custom: ['ห้ามซ้ำกับที่มีในระบบ'],
    },
    'license_number': {
        label: 'เลขที่ใบอนุญาต',
        required: true,
        maxLength: 50,
        custom: ['ห้ามซ้ำกับที่มีในระบบ'],
    },

    // Product fields
    'product_code': {
        label: 'รหัสผลิตภัณฑ์',
        required: true,
        maxLength: 50,
        custom: ['ห้ามซ้ำกับที่มีในระบบ'],
    },
    'product_name': {
        label: 'ชื่อผลิตภัณฑ์',
        required: true,
        maxLength: 255,
    },

    // Team fields
    'team_name': {
        label: 'ชื่อทีม',
        required: true,
        maxLength: 100,
    },
    'team_leader_id': {
        label: 'หัวหน้าทีม',
    },

    // Product fields
    'productName': {
        label: 'ชื่อผลิตภัณฑ์',
        required: true,
        maxLength: 255,
    },

    // Quotation fields
    'quotation_date': {
        label: 'วันที่ใบเสนอราคา',
        required: true,
    },
    'quotation_number': {
        label: 'เลขที่ใบเสนอราคา',
        required: true,
        pattern: /^QT-\d{6}-\d{4}$/,
        custom: ['รูปแบบ: QT-xxxxxx-xxxx'],
    },
    'customer_id': {
        label: 'ลูกค้า',
        required: true,
    },
    'insurance_type': {
        label: 'ประเภทประกันภัย',
        required: true,
    },
    'products': {
        label: 'รายการผลิตภัณฑ์',
        required: true,
        custom: ['ต้องมีอย่างน้อย 1 รายการ'],
    },
    'quantity': {
        label: 'จำนวน',
        required: true,
        custom: ['ต้องมากกว่า 0'],
    },
    'premium': {
        label: 'เบี้ยประกันภัย',
        required: true,
        custom: ['ต้องเป็นตัวเลขและมากกว่า 0'],
    },
    'payment_type': {
        label: 'ประเภทการชำระเงิน',
        required: true,
    },
    'total_premium': {
        label: 'เบี้ยประกันรวม',
        required: true,
        custom: ['ต้องเป็นตัวเลขและมากกว่า 0'],
    },
    'job_number': {
        label: 'เลขที่งาน',
        pattern: /^JOB-\d{6}-\d{4}$/,
        custom: ['รูปแบบ: JOB-xxxxxx-xxxx'],
    },

    // Department fields
    'department_name': {
        label: 'ชื่อแผนก',
        required: true,
        maxLength: 100,
    },
};

/**
 * Get validation message for a specific field and error type
 */
export function getValidationMessage(
    field: string,
    errorType: string,
    context?: any
): ValidationMessage {
    const fieldConfig = FIELD_VALIDATION_MAP[field];
    const fieldLabel = fieldConfig?.label || field;

    switch (errorType) {
        case 'missing':
        case 'required':
            return {
                error: `${fieldLabel}: จำเป็นต้องกรอก`,
                example: getFieldExample(field),
            };

        case 'string_too_long':
            return {
                error: `${fieldLabel}: ข้อความยาวเกินไป (สูงสุด ${context?.max_length || fieldConfig?.maxLength} ตัวอักษร)`,
            };

        case 'string_too_short':
            return {
                error: `${fieldLabel}: ข้อความสั้นเกินไป (ต้องมีอย่างน้อย ${context?.min_length || fieldConfig?.minLength} ตัวอักษร)`,
            };

        case 'string_pattern_mismatch':
        case 'value_error':
        case 'value_error.email':
            return getPatternErrorMessage(field, fieldLabel);

        case 'type_error':
            return {
                error: `${fieldLabel}: ประเภทข้อมูลไม่ถูกต้อง`,
            };

        case 'duplicate':
        case 'conflict':
            return {
                error: `${fieldLabel}: มีอยู่ในระบบแล้ว`,
                helpText: context?.conflictingRecord
                    ? `พบข้อมูลซ้ำกับ: ${context.conflictingRecord}`
                    : undefined,
            };

        default:
            return {
                error: `${fieldLabel}: ${context?.message || 'ข้อมูลไม่ถูกต้อง'}`,
                example: getFieldExample(field),
            };
    }
}

/**
 * Get pattern-specific error message
 */
function getPatternErrorMessage(field: string, fieldLabel: string): ValidationMessage {
    const examples: Record<string, { error: string; example: string }> = {
        'tax_id': {
            error: `${fieldLabel}: ต้องเป็นตัวเลข 13 หลัก`,
            example: 'เช่น 1234567890123',
        },
        'id_card_number': {
            error: `${fieldLabel}: ต้องเป็นตัวเลข 13 หลัก`,
            example: 'เช่น 1234567890123',
        },
        'email': {
            error: `${fieldLabel}: รูปแบบอีเมลไม่ถูกต้อง`,
            example: 'เช่น example@company.com',
        },
        'phone': {
            error: `${fieldLabel}: รูปแบบเบอร์โทรไม่ถูกต้อง`,
            example: 'เช่น 02-123-4567 หรือ 081-234-5678',
        },
        'postal_code': {
            error: `${fieldLabel}: ต้องเป็นตัวเลข 5 หลัก`,
            example: 'เช่น 10110',
        },
    };

    return examples[field] || {
        error: `${fieldLabel}: รูปแบบไม่ถูกต้อง`,
        example: getFieldExample(field),
    };
}

/**
 * Get example value for a field
 */
export function getFieldExample(field: string): string | undefined {
    const examples: Record<string, string> = {
        'tax_id': 'เช่น 1234567890123',
        'id_card_number': 'เช่น 1234567890123',
        'company_code': 'เช่น ABC001',
        'agent_code': 'เช่น AG001',
        'product_code': 'เช่น PROD001',
        'email': 'เช่น example@company.com',
        'phone': 'เช่น 02-123-4567',
        'postal_code': 'เช่น 10110',
        'license_number': 'เช่น 123456789',
    };

    return examples[field];
}

/**
 * Get validation rules for field hints
 */
export function getFieldValidationRules(field: string): string[] {
    const config = FIELD_VALIDATION_MAP[field];
    if (!config) return [];

    const rules: string[] = [];

    if (config.required) {
        rules.push('จำเป็นต้องกรอก');
    }

    if (config.custom) {
        rules.push(...config.custom);
    }

    if (config.maxLength) {
        rules.push(`ความยาวสูงสุด ${config.maxLength} ตัวอักษร`);
    }

    if (config.minLength) {
        rules.push(`ความยาวขั้นต่ำ ${config.minLength} ตัวอักษร`);
    }

    return rules;
}

/**
 * Get field label (Thai translation)
 */
export function getFieldLabel(field: string): string {
    return FIELD_VALIDATION_MAP[field]?.label || field;
}

/**
 * Check if field has validation rules
 */
export function hasValidationRules(field: string): boolean {
    return field in FIELD_VALIDATION_MAP;
}
